import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Timer } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { requestDiscountCode } from "@/lib/growth.functions";

const STORE_KEY = "momentum.discount";

interface Issued {
  code: string;
  percent: number;
  expiresAt: string;
}

function useCountdown(target: string | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  return useMemo(() => {
    if (!target) return null;
    const ms = new Date(target).getTime() - now;
    if (ms <= 0) return null;
    const h = Math.floor(ms / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return [h, m, s].map((n) => String(n).padStart(2, "0"));
  }, [target, now]);
}

export function DiscountOffer() {
  const request = useServerFn(requestDiscountCode);
  const [email, setEmail] = useState("");
  const [issued, setIssued] = useState<Issued | null>(null);
  const [status, setStatus] = useState<"idle" | "sending" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Issued;
      if (new Date(parsed.expiresAt).getTime() > Date.now()) setIssued(parsed);
      else localStorage.removeItem(STORE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const clock = useCountdown(issued?.expiresAt ?? null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("sending");
    setMessage(null);
    try {
      const res = await request({ data: { email } });
      if (!res.ok) {
        setStatus("error");
        setMessage(res.message);
        return;
      }
      const next = { code: res.code, percent: res.percent, expiresAt: res.expiresAt };
      setIssued(next);
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
      setStatus("idle");
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Try again in a moment.");
    }
  }

  async function copy() {
    if (!issued) return;
    await navigator.clipboard.writeText(issued.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  if (issued) {
    return (
      <div className="vault-panel p-7">
        <p className="micro-label">Your code is live</p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="etched rounded-sm border border-border px-4 py-3 font-mono text-xl font-semibold tracking-[0.2em]">
            {issued.code}
          </span>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-2 rounded-sm border border-border px-4 py-3 text-sm transition-colors hover:border-foreground/40"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          {issued.percent}% off your first year. Apply it at checkout with the email you entered.
        </p>
        {clock ? (
          <p className="mt-4 inline-flex items-center gap-2 text-sm tabular-nums text-foreground/80">
            <Timer className="h-3.5 w-3.5 text-muted-foreground" />
            Expires in {clock[0]}:{clock[1]}:{clock[2]}
          </p>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground">This code has expired — request a fresh one tomorrow.</p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="vault-panel p-7">
      <p className="micro-label">Early-access discount</p>
      <h3 className="etched mt-4 text-2xl font-semibold">20% off, held for 24 hours.</h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        Drop your email and we'll issue a private code on the spot, tied to that address. It runs for
        one day — the discount doesn't come back around.
      </p>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          aria-label="Email address"
          className="flex-1 rounded-sm border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-foreground/40"
        />
        <button
          type="submit"
          disabled={status === "sending"}
          className="sheen inline-flex items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        >
          {status === "sending" ? "Issuing…" : "Get my code"}
        </button>
      </div>
      {message && <p className="mt-3 text-sm text-muted-foreground">{message}</p>}
      <p className="mt-3 text-xs text-muted-foreground/70">
        One code per email. No list sharing, no follow-up spam.
      </p>
    </form>
  );
}
